import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// LLC API
export const llcApi = {
  list: () => axios.get(`${API}/llcs`),
  get: (id) => axios.get(`${API}/llcs/${id}`),
  create: (data) => axios.post(`${API}/llcs`, data),
  update: (id, data) => axios.put(`${API}/llcs/${id}`, data),
  delete: (id) => axios.delete(`${API}/llcs/${id}`),
  getChecks: (id) => axios.get(`${API}/llcs/${id}/checks`),
  getReportPdf: (id) => axios.get(`${API}/llcs/${id}/report/pdf`, { responseType: 'blob' }),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/llcs/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (id) => axios.delete(`${API}/llcs/${id}/logo`),
  getLogo: (id) => axios.get(`${API}/llcs/${id}/logo`, { responseType: 'blob' }),
  uploadSignature: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/llcs/${id}/signature`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteSignature: (id) => axios.delete(`${API}/llcs/${id}/signature`),
  getSignature: (id) => axios.get(`${API}/llcs/${id}/signature`, { responseType: 'blob' }),
};

// Bank API
export const bankApi = {
  list: () => axios.get(`${API}/banks`),
  get: (id) => axios.get(`${API}/banks/${id}`),
  create: (data) => axios.post(`${API}/banks`, data),
  update: (id, data) => axios.put(`${API}/banks/${id}`, data),
  delete: (id) => axios.delete(`${API}/banks/${id}`),
  getTransactions: (id) => axios.get(`${API}/banks/${id}/transactions`),
};

// Payee API
export const payeeApi = {
  list: (llcId = null, includeArchived = false) => axios.get(`${API}/payees`, { params: { ...(llcId ? { llc_id: llcId } : {}), include_archived: includeArchived } }),
  get: (id) => axios.get(`${API}/payees/${id}`),
  create: (data) => axios.post(`${API}/payees`, data),
  update: (id, data) => axios.put(`${API}/payees/${id}`, data),
  delete: (id) => axios.delete(`${API}/payees/${id}`),
  get1099Data: (id, year) => axios.get(`${API}/payees/${id}/1099/${year}`),
  exportExcel: () => axios.get(`${API}/payees/export/excel`, { responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/payees/import/excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAll1099Data: (year) => axios.get(`${API}/payees/1099-all/${year}`),
  getTransactions: (payeeName) => axios.get(`${API}/payees/${encodeURIComponent(payeeName)}/transactions`),
  toggleArchive: (id) => axios.put(`${API}/payees/${id}/archive`),
};

// Check API
export const checkApi = {
  list: (llcId = null) => axios.get(`${API}/checks`, { params: llcId ? { llc_id: llcId } : {} }),
  get: (id) => axios.get(`${API}/checks/${id}`),
  create: (data) => axios.post(`${API}/checks`, data),
  updateStatus: (id, status, voidReason = null) => axios.put(`${API}/checks/${id}/status`, { status, void_reason: voidReason }),
  batchVoid: (checkIds, voidReason) => axios.post(`${API}/checks/batch-void`, { check_ids: checkIds, void_reason: voidReason }),
  checkDuplicates: (data) => axios.post(`${API}/checks/duplicate-check`, data),
  delete: (id) => axios.delete(`${API}/checks/${id}`),
};

// Wire API
export const wireApi = {
  list: (llcId = null) => axios.get(`${API}/wires`, { params: llcId ? { llc_id: llcId } : {} }),
  get: (id) => axios.get(`${API}/wires/${id}`),
  create: (data) => axios.post(`${API}/wires`, data),
  update: (id, data) => axios.put(`${API}/wires/${id}`, data),
  updateStatus: (id, status) => axios.put(`${API}/wires/${id}/status?status=${status}`),
  delete: (id) => axios.delete(`${API}/wires/${id}`),
  email: (id, toEmail) => axios.post(`${API}/wires/${id}/email`, { to_email: toEmail }),
};

// Wire Template API
export const wireTemplateApi = {
  list: () => axios.get(`${API}/wire-templates`),
  create: (data) => axios.post(`${API}/wire-templates`, data),
  update: (id, data) => axios.put(`${API}/wire-templates/${id}`, data),
  delete: (id) => axios.delete(`${API}/wire-templates/${id}`),
};

// User API
export const userApi = {
  list: () => axios.get(`${API}/users`),
  create: (data) => axios.post(`${API}/users`, data),
  update: (id, data) => axios.put(`${API}/users/${id}`, data),
  delete: (id) => axios.delete(`${API}/users/${id}`),
  emulate: (id) => axios.post(`${API}/users/${id}/emulate`),
};

// Activity Log API
export const activityLogApi = {
  list: (limit = 100, userId = null) => axios.get(`${API}/activity-logs`, { 
    params: { limit, ...(userId ? { user_id: userId } : {}) }
  }),
  exportCsv: () => axios.get(`${API}/activity-logs/export/csv`, { responseType: 'blob' }),
};

// Check Layout API
export const checkLayoutApi = {
  get: () => axios.get(`${API}/check-layout`),
  update: (data) => axios.put(`${API}/check-layout`, data),
  reset: () => axios.post(`${API}/check-layout/reset`),
};

// ACH API
export const achApi = {
  list: (llcId = null) => axios.get(`${API}/ach`, { params: llcId ? { llc_id: llcId } : {} }),
  get: (id) => axios.get(`${API}/ach/${id}`),
  create: (data) => axios.post(`${API}/ach`, data),
  update: (id, data) => axios.put(`${API}/ach/${id}`, data),
  updateStatus: (id, status) => axios.put(`${API}/ach/${id}/status?status=${status}`),
  delete: (id) => axios.delete(`${API}/ach/${id}`),
};

// ACH Template API
export const achTemplateApi = {
  list: () => axios.get(`${API}/ach-templates`),
  create: (data) => axios.post(`${API}/ach-templates`, data),
  update: (id, data) => axios.put(`${API}/ach-templates/${id}`, data),
  delete: (id) => axios.delete(`${API}/ach-templates/${id}`),
};

// Payee Analytics API
export const payeeAnalyticsApi = {
  getSpending: (llcId = null) => axios.get(`${API}/analytics/payee-spending`, { params: llcId ? { llc_id: llcId } : {} }),
};

// Auth API
export const authApi = {
  logout: () => axios.post(`${API}/auth/logout`),
  changePassword: (data) => axios.put(`${API}/auth/change-password`, data),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API}/dashboard/stats`),
};

// Backup & Restore API
export const backupApi = {
  create: () => axios.get(`${API}/backup`),
  restore: (data) => axios.post(`${API}/restore`, data),
};


// Payment Export API
export const paymentApi = {
  exportExcel: (params) => axios.get(`${API}/payments/export/excel`, { params, responseType: 'blob' }),
};

// App Settings API
export const appSettingsApi = {
  get: () => axios.get(`${API}/app-settings`),
  update: (data) => axios.put(`${API}/app-settings`, data),
};

// SMTP Settings API
export const smtpApi = {
  get: () => axios.get(`${API}/smtp-settings`),
  update: (data) => axios.put(`${API}/smtp-settings`, data),
  test: () => axios.post(`${API}/smtp-settings/test`),
};

// Backup API
export const backupScheduleApi = {
  get: () => axios.get(`${API}/backup-schedule`),
  update: (data) => axios.put(`${API}/backup-schedule`, data),
  sendNow: () => axios.post(`${API}/backup/send-now`),
};

// Security Settings API
export const securityApi = {
  get: () => axios.get(`${API}/security-settings`),
  update: (data) => axios.put(`${API}/security-settings`, data),
};
